import { Component, Inject, ViewChild, forwardRef } from "@angular/core";
import { NgForm } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import * as _ from 'lodash';
import { ErrorMessages } from "../../common/constants";
import { LocationSchema, ProductionLine, WorkCell } from "../../model";
import { AddTagDialogComponent } from "../../shared/component/tag-editor/add-tag-dialog.component";
import { ErrorUtility } from "../../utility/error-utility";
import { LocationSchemaService } from "./location-schema-widget.service";

@Component({
    selector: 'location-schema-widget-add-element-dialog',
    template: require('./location-schema-widget-add-element-dialog.component.html'),
    providers: [LocationSchemaService]
})
export class LocationSchemaWidgetAddElementDialogComponent {

    @ViewChild('form') form: NgForm;

    title: string;
    error: string;
    data: LocationSchemaAddElementDialogBody;
    currentElement: ProductionLine | WorkCell;
    updating: boolean;
    invalidNameMessage: string;

    private invalidNames: string[];

    constructor(
        @Inject(forwardRef(() => MatDialogRef)) public dialogRef: MatDialogRef<AddTagDialogComponent>,
        @Inject(MAT_DIALOG_DATA) data,
        @Inject(forwardRef(() => LocationSchemaService)) private locationSchemaService: LocationSchemaService
    ) {
        this.data = _.cloneDeep(data);
        this.validateData();
        this.currentElement = this.getCurrentElement();
        this.title = this.getTitle();
        this.invalidNames = this.getInvalidNames();
    }

    private validateData(): void {
        switch (this.data.operationType) {
            case LocationSchemaAddElementDialogOperationType.ADD_PRODUCTION_LINE:
                break;
            case LocationSchemaAddElementDialogOperationType.EDIT_PRODUCTION_LINE:
            case LocationSchemaAddElementDialogOperationType.ADD_WORK_CELL:
                if (this.data.productionLineIndex == null) {
                    this.error = 'Invalid data: missing productionLineIndex';
                }
                break;
            case LocationSchemaAddElementDialogOperationType.EDIT_WORK_CELL:
                if (this.data.productionLineIndex == null || this.data.workCellIndex == null) {
                    this.error = 'Invalid data: missing productionLineIndex or workCellIndex';
                }
                break;
            default:
                this.error = 'Invalid Operation Type';
        }
    }

    private getTitle(): string {
        switch (this.data.operationType) {
            case LocationSchemaAddElementDialogOperationType.ADD_PRODUCTION_LINE:
                return 'addProductionLineProperty';
            case LocationSchemaAddElementDialogOperationType.EDIT_PRODUCTION_LINE:
                return 'editProductionLineProperty';
            case LocationSchemaAddElementDialogOperationType.ADD_WORK_CELL:
                return 'addWorkCellProperty';
            case LocationSchemaAddElementDialogOperationType.EDIT_WORK_CELL:
                return 'editWorkCellProperty';
            default:
                this.error = 'Invalid Operation Type';
                return 'Invalid Operation Type';
        }
    }

    private getCurrentElement(): ProductionLine | WorkCell {
        const productionLineIndex = this.data.productionLineIndex;
        const workCellIndex = this.data.workCellIndex;
        switch (this.data.operationType) {
            case LocationSchemaAddElementDialogOperationType.ADD_PRODUCTION_LINE:
            case LocationSchemaAddElementDialogOperationType.EDIT_PRODUCTION_LINE:
                return productionLineIndex != null ? this.data.locationSchema.productionLines[productionLineIndex] : null;
            case LocationSchemaAddElementDialogOperationType.ADD_WORK_CELL:
            case LocationSchemaAddElementDialogOperationType.EDIT_WORK_CELL:
                const productionLine = productionLineIndex != null ? this.data.locationSchema.productionLines[productionLineIndex] : null;
                return workCellIndex != null && productionLine != null ? productionLine.workCells[workCellIndex] : null;
        }
    }

    private getInvalidNames(): string[] {
        const productionLineIndex = this.data.productionLineIndex;
        switch (this.data.operationType) {
            case LocationSchemaAddElementDialogOperationType.ADD_PRODUCTION_LINE:
                return this.data.locationSchema.productionLines?.map(pl => pl.name);
            case LocationSchemaAddElementDialogOperationType.EDIT_PRODUCTION_LINE:
                return this.data.locationSchema.productionLines?.map(pl => pl.name).filter(name => name != this.currentElement.name);
            case LocationSchemaAddElementDialogOperationType.ADD_WORK_CELL:
                return this.data.locationSchema.productionLines[productionLineIndex].workCells?.map(wc => wc.name);
            case LocationSchemaAddElementDialogOperationType.EDIT_WORK_CELL:
                return this.data.locationSchema.productionLines[productionLineIndex].workCells?.map(wc => wc.name).filter(name => name != this.currentElement.name);
        }
    }

    save(): void {
        this.updating = true;
        let locationSchema: LocationSchema = this.data.locationSchema;
        const rawValues = this.form.form.getRawValue();
        switch (this.data.operationType) {
            case LocationSchemaAddElementDialogOperationType.ADD_PRODUCTION_LINE:
                this.addProductionLine(locationSchema, rawValues);
                break;
            case LocationSchemaAddElementDialogOperationType.EDIT_PRODUCTION_LINE:
                this.updateProductionLine(locationSchema, rawValues);
                break;
            case LocationSchemaAddElementDialogOperationType.ADD_WORK_CELL:
                this.addWorkCell(locationSchema, rawValues);
                break;
            case LocationSchemaAddElementDialogOperationType.EDIT_WORK_CELL:
                this.updateWorkCell(locationSchema, rawValues);
                break;
        }
        this.locationSchemaService.saveLocationSchema(locationSchema).then(updatedLocationSchema => {
            this.updating = false;
            this.dialogRef.close(updatedLocationSchema);
        }).catch(err => {
            this.error = ErrorUtility.getMessage(err, ErrorMessages.SAVE_DATA_ERROR);
            this.updating = false;
        });
    }

    private addProductionLine(locationSchema: LocationSchema, rawValues: any): void {
        if (!locationSchema.productionLines) {
            locationSchema.productionLines = [];
        }
        const newProductionLine: ProductionLine = {
            id: null,
            name: rawValues.name,
            workCells: null
        }
        locationSchema.productionLines.push(newProductionLine);
    }

    private updateProductionLine(locationSchema: LocationSchema, rawValues: any): void {
        let currentProductionLine = locationSchema.productionLines[this.data.productionLineIndex];
        currentProductionLine.name = rawValues.name;
    }

    private addWorkCell(locationSchema: LocationSchema, rawValues: any): void {
        if (!locationSchema.productionLines[this.data.productionLineIndex].workCells) {
            locationSchema.productionLines[this.data.productionLineIndex].workCells = [];
        }
        const newWorkCell: WorkCell = {
            id: null,
            name: rawValues.name,
            thingIds: null
        }
        locationSchema.productionLines[this.data.productionLineIndex].workCells.push(newWorkCell);
    }

    private updateWorkCell(locationSchema: LocationSchema, rawValues: any): void {
        let currentWorkCell = locationSchema.productionLines[this.data.productionLineIndex].workCells[this.data.workCellIndex];
        currentWorkCell.name = rawValues.name;
    }

    isNameInvalid(): boolean {
        if (this.form) {
            const rawValues = this.form.form.getRawValue();
            if (rawValues.name == ' ') {
                this.invalidNameMessage = 'Invalid name';
                return true;
            } else if (this.invalidNames?.includes(rawValues.name)) {
                this.invalidNameMessage = this.getInvalidNameErrorMessage();
                return true;
            } else {
                this.invalidNameMessage = null;
                return false;
            }
        } else {
            this.invalidNameMessage = null;
            return true;
        }
    }

    private getInvalidNameErrorMessage(): string {
        switch (this.data.operationType) {
            case LocationSchemaAddElementDialogOperationType.ADD_PRODUCTION_LINE:
            case LocationSchemaAddElementDialogOperationType.EDIT_PRODUCTION_LINE:
                return 'Production Line already present';
            case LocationSchemaAddElementDialogOperationType.ADD_WORK_CELL:
            case LocationSchemaAddElementDialogOperationType.EDIT_WORK_CELL:
                return 'Work Cell already present';
        }
    }

}

export class LocationSchemaAddElementDialogBody {
    locationSchema: LocationSchema;
    operationType: LocationSchemaAddElementDialogOperationType;
    productionLineIndex: number;
    workCellIndex: number;
}

export enum LocationSchemaAddElementDialogOperationType {
    ADD_PRODUCTION_LINE = 'ADD_PRODUCTION_LINE',
    EDIT_PRODUCTION_LINE = 'EDIT_PRODUCTION_LINE',
    ADD_WORK_CELL = 'ADD_WORK_CELL',
    EDIT_WORK_CELL = 'EDIT_WORK_CELL'
}